/**
 * DebugActionsPanel — copy-id / kill-session links + the SignalR ping
 * diagnostic, combined into ONE block for the More Information pane.
 *
 * Both used to live in the primary/running body (ora: the sticky footer +
 * a block above it; the add-in: unconditionally inside the scrollable
 * body) — always-visible dev/triage tooling that isn't part of what an AM
 * needs to see while working a run. Moved behind the more-information entry
 * point (running-drawer AC-7) so ora and the plugin render the identical
 * block in the identical place.
 *
 * Rendered by both hosts inside the more-information entry point. The older
 * always-visible placements (a copy row in the footer, an inline SignalR
 * block) are gone with the flag that gated them.
 *
 * Import alias in ora:     ~/common/workflows/drawer/DebugActionsPanel
 * Import alias in add-in:  @product-app/common/workflows/drawer/DebugActionsPanel
 */

import { useState } from "react";

export interface DebugActionsPanelProps {
  instanceId?: string | null;
  onCopyInstance?: () => void;
  onCopyCorrelation: () => void;
  sessionId?: string | null;
  onCopySession?: () => void;
  onKillSession?: () => Promise<void> | void;
  isLiveConnected: boolean;
  pingMessages: Array<{ sender: string; message: string }>;
  pingSending: boolean;
  onSendPing: () => Promise<void> | void;
}

export function DebugActionsPanel({
  instanceId,
  onCopyInstance,
  onCopyCorrelation,
  sessionId,
  onCopySession,
  onKillSession,
  isLiveConnected,
  pingMessages,
  pingSending,
  onSendPing,
}: DebugActionsPanelProps) {
  const [killingSession, setKillingSession] = useState(false);

  const handleKillSession = async () => {
    if (!onKillSession) return;
    setKillingSession(true);
    try {
      await onKillSession();
    } finally {
      setKillingSession(false);
    }
  };

  const showSignalR = isLiveConnected || pingMessages.length > 0;

  return (
    <div className="mb-2 flex flex-col gap-2 rounded-md border border-primary-gray-light-200 bg-primary-gray-light-25 p-3">
      <div className="flex flex-wrap items-center gap-3 text-2xs">
        {instanceId && onCopyInstance && (
          <button
            type="button"
            onClick={onCopyInstance}
            className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
            data-testid="copy-instance"
            title="Workflow instance id — the durable id stored in workflow_instances, DT history, MLflow traces, and the SignalR group name"
          >
            Copy instance id
          </button>
        )}
        <button
          type="button"
          onClick={onCopyCorrelation}
          className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
          data-testid="copy-correlation"
          title="Correlation id — paste into product API / substrate log search"
        >
          Copy correlation id
        </button>
        {sessionId && (
          <>
            {onCopySession && (
              <button
                type="button"
                onClick={onCopySession}
                className="text-primary-gray-light-600 hover:text-primary-gray-light-900 underline"
                data-testid="copy-session"
                title="Exec-plane RPA session token — paste into log search for browser-side triage"
              >
                Copy session id
              </button>
            )}
            {onKillSession && (
              <button
                type="button"
                onClick={() => void handleKillSession()}
                disabled={killingSession}
                className="text-primary-error-700 hover:text-primary-error-900 underline disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="kill-session"
                title="Debug only — DELETE /sessions/{token} against the exec-plane to free a stuck browser session"
              >
                {killingSession ? "Killing…" : "Kill session"}
              </button>
            )}
          </>
        )}
      </div>
      {showSignalR && (
        <div data-testid="signalr-test">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-2xs font-semibold text-primary-gray-light-500 uppercase tracking-wide">
              SignalR Test
            </span>
            <button
              type="button"
              onClick={() => void onSendPing()}
              disabled={pingSending || !isLiveConnected}
              className="px-2 py-0.5 rounded border border-primary-gray-light-300 text-2xs text-primary-gray-light-700 hover:bg-primary-gray-light-100 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {pingSending ? "Sending…" : "Send Hello"}
            </button>
          </div>
          {pingMessages.length > 0 ? (
            <div className="flex flex-col gap-0.5">
              {pingMessages.map((m, i) => (
                <div
                  key={i}
                  className="text-2xs text-primary-gray-light-600 font-mono truncate"
                >
                  ← <span className="font-semibold">{m.sender}</span>: {m.message}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-2xs text-primary-gray-light-400 italic">
              Waiting for messages…
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default DebugActionsPanel;
